import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { id } from 'date-fns/locale';
import billService, { Bill, CreateBillData } from '../services/billService';

interface EditBillDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bill: Bill;
}

const EditBillDialog: React.FC<EditBillDialogProps> = ({
  open,
  onClose,
  onSuccess,
  bill,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<Partial<CreateBillData>>({
    billing_period_start: '',
    billing_period_end: '',
    amount: 0,
    tax_amount: 0,
    discount_amount: 0,
    notes: '',
  });

  useEffect(() => {
    if (open && bill) {
      setFormData({
        billing_period_start: bill.billing_period_start,
        billing_period_end: bill.billing_period_end,
        amount: bill.amount,
        tax_amount: bill.tax_amount,
        discount_amount: bill.discount_amount,
        notes: bill.notes || '',
      });
      setError(null);
    }
  }, [open, bill]);

  const handleInputChange = (field: keyof CreateBillData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Auto calculate due date when billing period end changes
    if (field === 'billing_period_end') {
      const endDate = new Date(value);
      const dueDate = new Date(endDate.getTime() + 7 * 24 * 60 * 60 * 1000);
      setFormData(prev => ({
        ...prev,
        due_date: dueDate.toISOString().split('T')[0],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      const payload: Partial<CreateBillData> & { due_date?: string } = {
        billing_period_start: formData.billing_period_start,
        billing_period_end: formData.billing_period_end,
        due_date:
          (formData as { due_date?: string }).due_date || formData.billing_period_start,
        amount: Number(formData.amount) || 0,
        tax_amount: Number(formData.tax_amount) || 0,
        discount_amount: Number(formData.discount_amount) || 0,
        notes: formData.notes || '',
      };

      await billService.updateBill(bill.id, payload);
      onSuccess();
    } catch (err: any) {
      const apiErrors = err.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        setError(apiErrors.map((e: { msg?: string }) => e.msg).filter(Boolean).join('. '));
      } else {
        setError(err.response?.data?.message || 'Gagal mengupdate tagihan');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
    }).format(amount);
  };

  const totalAmount = (formData.amount || 0) + (formData.tax_amount || 0) - (formData.discount_amount || 0);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={id}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Tagihan - {bill.bill_number}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Customer and Package Info (Read Only) */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Informasi Customer & Paket
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Typography variant="body2" color="textSecondary">
                    Customer:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {bill.customer?.name} ({bill.customer?.customer_code})
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Typography variant="body2" color="textSecondary">
                    Paket:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {bill.package?.name}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <DatePicker
                    label="Periode Mulai"
                    value={formData.billing_period_start ? new Date(formData.billing_period_start) : null}
                    onChange={(date) => 
                      handleInputChange('billing_period_start', date?.toISOString().split('T')[0] || '')
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      }
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <DatePicker
                    label="Periode Berakhir"
                    value={formData.billing_period_end ? new Date(formData.billing_period_end) : null}
                    onChange={(date) => 
                      handleInputChange('billing_period_end', date?.toISOString().split('T')[0] || '')
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Alert severity="info">
                    Due date will be automatically set to customer's activation date
                  </Alert>
                </Box>

                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Jumlah Tagihan"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                    required
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Pajak"
                    type="number"
                    value={formData.tax_amount}
                    onChange={(e) => handleInputChange('tax_amount', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Diskon"
                    type="number"
                    value={formData.discount_amount}
                    onChange={(e) => handleInputChange('discount_amount', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold' }}>Total Tagihan:</span>
                  <span style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                    {formatCurrency(totalAmount)}
                  </span>
                </div>
              </Box>

              <TextField
                fullWidth
                label="Catatan"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Batal</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EditBillDialog;
